<div class="container mx-auto px-4 py-8 max-w-4xl">
  <!-- Header -->
  <div class="mb-8">
    <h1 class="text-3xl font-bold text-gray-800 dark:text-white mb-2">
      Export Your Data
    </h1>
    <p class="text-gray-600 dark:text-gray-400">
      Download a copy of all your personal data stored in Wall St Yardie. This includes your profile information, watchlists, preferences, and activity history.
    </p>
  </div>

  <!-- Messages -->
  @if (errorMessage()) {
    <p-message severity="error" [text]="errorMessage()!" styleClass="mb-4 w-full"></p-message>
  }
  
  @if (successMessage()) {
    <p-message severity="success" [text]="successMessage()!" styleClass="mb-4 w-full"></p-message>
  }

  <!-- Request New Export -->
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
    <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
      Request New Export
    </h2>
    <p class="text-gray-600 dark:text-gray-400 mb-6">
      Choose your preferred format and we'll prepare your data for download. You'll receive an email notification when your export is ready.
    </p>
    
    <div class="flex flex-wrap gap-4">
      <p-button
        label="Export as JSON"
        icon="pi pi-download"
        severity="success"
        [loading]="isRequesting()"
        [disabled]="isRequesting()"
        (onClick)="requestExport('json')"
      ></p-button>
      
      <p-button
        label="Export as CSV"
        icon="pi pi-download"
        severity="success"
        [outlined]="true"
        [loading]="isRequesting()"
        [disabled]="isRequesting()"
        (onClick)="requestExport('csv')"
      ></p-button>
    </div>
    
    <div class="mt-4 text-sm text-gray-500 dark:text-gray-400">
      <p><strong>JSON format:</strong> Complete structured data, ideal for technical users or data migration.</p>
      <p><strong>CSV format:</strong> Spreadsheet-compatible format, easy to view in Excel or Google Sheets.</p>
    </div>
  </div>

  <!-- Pending Requests -->
  @if (pendingRequests().length > 0) {
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        Pending Requests
      </h2>
      
      <div class="space-y-4">
        @for (request of pendingRequests(); track request.id) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div class="flex justify-between items-center">
              <div>
                <p class="font-medium text-gray-800 dark:text-white">
                  {{ request.format.toUpperCase() }} Export
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Requested: {{ request.requestedAt | date:'short' }}
                </p>
              </div>
              <div>
                <span [class]="getStatusClass(request.status)" class="font-semibold">
                  {{ getStatusLabel(request.status) }}
                </span>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Completed Requests -->
  @if (completedRequests().length > 0) {
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        Available Downloads
      </h2>
      
      <div class="space-y-4">
        @for (request of completedRequests(); track request.id) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div class="flex justify-between items-center">
              <div>
                <p class="font-medium text-gray-800 dark:text-white">
                  {{ request.format.toUpperCase() }} Export
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Completed: {{ request.completedAt | date:'short' }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Downloads expire after 7 days
                </p>
              </div>
              <div>
                <p-button
                  label="Download"
                  icon="pi pi-download"
                  severity="success"
                  [outlined]="true"
                  (onClick)="downloadExport(request)"
                ></p-button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  @if (!isLoading() && pendingRequests().length === 0 && completedRequests().length === 0) {
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
      <i class="pi pi-inbox text-4xl text-gray-400 dark:text-gray-600 mb-4"></i>
      <p class="text-gray-600 dark:text-gray-400">
        No export requests yet. Request your first data export above.
      </p>
    </div>
  }

  <!-- Information -->
  <div class="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
    <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
      <i class="pi pi-info-circle mr-2"></i>About Data Exports
    </h3>
    <ul class="text-sm text-blue-800 dark:text-blue-200 space-y-2">
      <li>• Export requests are typically processed within 24 hours</li>
      <li>• You'll receive an email when your data is ready to download</li>
      <li>• Download links expire after 7 days for security</li>
      <li>• You can request a new export at any time</li>
      <li>• All exports are encrypted and secure</li>
    </ul>
  </div>
</div>
